import React, { ReactElement, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { isEqual } from 'lodash';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Media } from '../../../structures/models/media/media';
import { FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX, GOOGLE_RECAPTCHA_KEY } from '../../../utils/constants';
import TestimonialForm from '../../forms/TestimonialForm';
import { testimonialValidator } from '../../forms/validators/form-validators';

const Testimonials: React.FC<any> = (): ReactElement => {
  const [formValues, setFormValues] = useState<{
    name: string,
    email: string,
    body: string,
    breeder_phone: string,
    breeder_name: string,
    title: string,
    images: Array<Media>,
    id?: number,
  }>({
    name: '',
    email: '',
    title: '',
    body: '',
    breeder_name: '',
    breeder_phone: '',
    images: [],
  });
  const [loading, setLoading] = useState(false);
  const [cloudinaryIsUpdating, setCloudinaryIsUpdating] = useState(false);
  const [show, setShow] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  // we wrap executeRecaptcha because it might take a bit to initialize,
  // so we handle that possibility gracefully here
  const handleReCaptchaVerify = async () => {
    if (!executeRecaptcha) {
      return '';
    }

    return executeRecaptcha('submit');
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const updateFormState = useCallback((newValues: object) => {
    const newFormState = {
      ...formValues,
      ...newValues,
    };
    if (!isEqual(newFormState, formValues)) {
      setFormValues(newFormState);
    }
  }, [formValues]);

  const onSubmit = useCallback(async (values) => {
    updateFormState(values);
    const valuesWithRecaptcha = {
      recaptcha: await handleReCaptchaVerify(),
      ...values,
    };
    setLoading(true);
    axios.post(
      FORM_SUBMISSIONS_TESTIMONIALS_BROWSER_API_PREFIX,
      valuesWithRecaptcha,
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (response.data?.testimonial || (response.status >= 200 && response.status <= 299)) {
          Swal.fire(
            'Success',
            'Testimonial created successfully!',
            'success',
          ).then(() => {
            const newFormValues = {
              name: '',
              email: '',
              title: '',
              body: '',
              breeder_name: '',
              breeder_phone: '',
              images: [],
            };
            setFormValues(newFormValues);
            setLoading(false);
            handleClose();
          });
        }
      })
      .catch((e) => {
        // console.log(e);
        setLoading(false);
        Swal.fire(
          'Error',
          e?.message || 'Something went wrong; check your address and connection, then try again.',
          'error',
        );
      });
  }, [formValues, executeRecaptcha]);

  return (
    <>
      <button
        className="btn btn-blue-100 p-3 text-dark-gray badge badge-pill font-family-sans-serif nav-link"
        onClick={handleShow}
      >
        Tell Us About Your Experience
      </button>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="modal-lg"
        backdropClassName="bg-blue-200"
        scrollable={true}
      >
        <Modal.Body className="p-0">
          {show && (
            <>
              <TestimonialForm
                formValues={formValues}
                validator={testimonialValidator}
                handleClose={handleClose}
                onSubmit={onSubmit}
                loading={loading}
                canUpdateStatus={false}
                cloudinaryIsUpdating={cloudinaryIsUpdating}
                setCloudinaryIsUpdating={setCloudinaryIsUpdating}
              />
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Testimonials;

if (document.getElementById('react-testimonial-form')) {
  ReactDOM.render(
    <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_KEY || ''}>
      <Testimonials />
    </GoogleReCaptchaProvider>,
    document.getElementById('react-testimonial-form'),
  );
}
